import { FC, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { useAtlasAuthContext } from "@/contexts/atlas-auth";
import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { toastModule } from "@/modules/toast";
import {
  registerAudioRecordingUploadInternal,
  finalizeAudioRecordingUploadInternal,
} from "@/pages/audio-recordings/shared/api";
import { uploadSingleFile } from "@/pages/audio-recordings/recorder/upload-single-file";

const Container = styled.div`
  padding: 32px;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #4a5568;
`;

const Section = styled.section`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 14px;
  color: #4a5568;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #2d3748;
`;

const UploadArea = styled.div<{ isDragging?: boolean }>`
  border: 2px dashed ${({ isDragging }) => (isDragging ? "#3182ce" : "#cbd5e0")};
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  background: ${({ isDragging }) => (isDragging ? "#ebf8ff" : "#f7fafc")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #3182ce;
    background: #ebf8ff;
  }
`;

const UploadText = styled.p`
  margin: 0;
  color: #4a5568;
  font-size: 14px;
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #edf2f7;
  border-radius: 6px;
`;

const FileName = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: #718096;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatusMessage = styled.div<{ variant?: "success" | "error" | "info" }>`
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  background: ${({ variant }) =>
    variant === "success" ? "#c6f6d5" : variant === "error" ? "#fed7d7" : "#bee3f8"};
  color: ${({ variant }) =>
    variant === "success" ? "#22543d" : variant === "error" ? "#742a2a" : "#2a4365"};
`;

const HiddenInput = styled.input`
  display: none;
`;

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(1)} ${units[index]}`;
};

type ProcessingStep = "idle" | "creating" | "uploading" | "processing" | "done" | "error";

export const NotesUploadAudioPage: FC = () => {
  const navigate = useNavigate();
  const { noteId } = useParams<{ noteId: Uuid }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    authContext: { authToken },
  } = useAtlasAuthContext();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processingStep, setProcessingStep] = useState<ProcessingStep>("idle");
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [_uploadProgress, setUploadProgress] = useState<number>(0);
  const [createdSuggestionId, setCreatedSuggestionId] = useState<string | null>(null);

  const noteQuery = useQuery({
    queryKey: ["note", noteId],
    queryFn: () =>
      api.get("/internal/v1/notes/{note_id}", {
        params: { path: { note_id: noteId! } },
      }),
    enabled: Boolean(noteId),
  });

  const note = noteQuery.data?.data;

  const handleFileSelect = (file: File) => {
    const validTypes = [
      "audio/mpeg",
      "audio/wav",
      "audio/webm",
      "audio/mp4",
      "audio/m4a",
      "audio/ogg",
    ];
    if (!validTypes.includes(file.type) && !/\.(mp3|wav|webm|m4a|ogg)$/i.exec(file.name)) {
      toastModule.triggerToast({
        content: "Please select a valid audio file (mp3, wav, webm, m4a, ogg)",
      });
      return;
    }
    setSelectedFile(file);
    setProcessingStep("idle");
    setStatusMessage("");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setProcessingStep("idle");
    setStatusMessage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleProcess = async () => {
    if (!selectedFile || !noteId || !note?.owned_by_space_account_id || !authToken) {
      toastModule.triggerToast({ content: "Missing required data" });
      return;
    }

    const spaceAccountId = note.owned_by_space_account_id;
    const mimeType = selectedFile.type || "audio/mpeg";

    try {
      // Step 1: Create audio recording
      setProcessingStep("creating");
      setStatusMessage("Creating audio recording...");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const createResponse = await (api.post as any)("/internal/v1/audio-recordings/create", {
        body: {
          space_account_id: spaceAccountId,
          associated_note_id: noteId,
          value_json: { title: selectedFile.name, source: "atlas_upload" },
        },
      });

      const audioRecordingId = createResponse.data?.audio_recording?.id;
      if (!audioRecordingId) {
        throw new Error("Failed to create audio recording");
      }

      // Step 2: Register upload to get bucket info
      setProcessingStep("uploading");
      setStatusMessage("Preparing upload...");
      setUploadProgress(0);

      const registerResponse = await registerAudioRecordingUploadInternal(audioRecordingId, {
        space_account_id: spaceAccountId,
        filename: selectedFile.name,
        mime_type: mimeType,
      });

      if (!registerResponse) {
        throw new Error("Failed to register upload");
      }

      // Step 3: Upload via TUS
      setStatusMessage("Uploading audio file...");

      const uploadResult = await uploadSingleFile({
        file: selectedFile,
        mimeType,
        filename: registerResponse.normalized_filename,
        spaceAccountId: registerResponse.space_account_id,
        audioRecordingId: registerResponse.audio_recording_id,
        bucketName: registerResponse.bucket_name,
        authToken,
        onProgress: (bytesUploaded, totalBytes) => {
          const percent = Math.round((bytesUploaded / totalBytes) * 100);
          setUploadProgress(percent);
          setStatusMessage(`Uploading audio file... ${percent}%`);
        },
      });

      if (!uploadResult.success) {
        throw new Error(uploadResult.error ?? "Failed to upload audio file");
      }

      // Step 4: Finalize upload
      setStatusMessage("Finalizing upload...");

      const finalizeResponse = await finalizeAudioRecordingUploadInternal(audioRecordingId, {
        space_account_id: spaceAccountId,
        filename: registerResponse.normalized_filename,
        mime_type: mimeType,
      });

      if (!finalizeResponse?.success) {
        throw new Error("Failed to finalize upload");
      }

      // Step 5: Process to generate suggestion
      setProcessingStep("processing");
      setStatusMessage("Processing audio and generating suggestion...");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const processResponse = await (api.post as any)(
        `/internal/v1/audio-recordings/${audioRecordingId}/process`,
        {
          body: { note_id: noteId },
        }
      );

      if (processResponse.data?.suggestion_id) {
        setCreatedSuggestionId(processResponse.data.suggestion_id);
      }

      setProcessingStep("done");
      setStatusMessage("Audio processed successfully! A suggestion has been generated.");
      toastModule.triggerToast({ content: "Audio processed and suggestion generated!" });
    } catch (error) {
      setProcessingStep("error");
      const message = error instanceof Error ? error.message : "An error occurred";
      setStatusMessage(`Error: ${message}`);
      toastModule.triggerToast({ content: message });
    }
  };

  if (!noteId) {
    return <Container>Missing note ID</Container>;
  }

  return (
    <Container>
      <div>
        <Title>Upload Audio Recording</Title>
        <Subtitle>
          Upload an audio file to process and generate a suggestion for this note.
        </Subtitle>
      </div>

      <Section>
        <SectionTitle>Note Information</SectionTitle>
        {noteQuery.isLoading && <InfoRow>Loading note...</InfoRow>}
        {note && (
          <>
            <InfoRow>
              <InfoLabel>Note ID:</InfoLabel> {noteId}
            </InfoRow>
            <InfoRow>
              <InfoLabel>Owner Space Account:</InfoLabel> {note.owned_by_space_account_id}
            </InfoRow>
            <InfoRow>
              <InfoLabel>Primary Label:</InfoLabel> {note.primary_label || "(none)"}
            </InfoRow>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>Upload Audio File</SectionTitle>

        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.webm,.m4a,.ogg"
          onChange={handleInputChange}
        />

        {!selectedFile ? (
          <UploadArea
            isDragging={isDragging}
            onClick={handleUploadClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <UploadText>Click to select an audio file or drag and drop here</UploadText>
            <UploadText>Supported formats: MP3, WAV, WebM, M4A, OGG</UploadText>
          </UploadArea>
        ) : (
          <SelectedFile>
            <FileName>{selectedFile.name}</FileName>
            <FileSize>{formatBytes(selectedFile.size)}</FileSize>
            <Button type="button" variant="soft" onClick={handleClearFile}>
              Remove
            </Button>
          </SelectedFile>
        )}
      </Section>

      {statusMessage && (
        <StatusMessage
          variant={
            processingStep === "done" ? "success" : processingStep === "error" ? "error" : "info"
          }
        >
          {statusMessage}
        </StatusMessage>
      )}

      <ButtonRow>
        <Button
          type="button"
          onClick={handleProcess}
          disabled={
            !selectedFile ||
            processingStep === "creating" ||
            processingStep === "uploading" ||
            processingStep === "processing"
          }
        >
          {processingStep === "creating"
            ? "Creating..."
            : processingStep === "uploading"
              ? "Uploading..."
              : processingStep === "processing"
                ? "Processing..."
                : "Process Audio"}
        </Button>

        {createdSuggestionId && (
          <Button
            type="button"
            onClick={() =>
              navigate(
                atlasRoutes.noteSuggestionsView({
                  params: { noteSuggestionId: createdSuggestionId },
                }).path
              )
            }
          >
            View Suggestion
          </Button>
        )}

        <Button
          type="button"
          variant="soft"
          onClick={() => navigate(atlasRoutes.notesView({ params: { noteId } }).path)}
        >
          Back to Note
        </Button>
      </ButtonRow>
    </Container>
  );
};
